import React, { useCallback, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ref, get } from "firebase/database";
import { db } from "../../../firebaseConfig";
import {
  aggregateDailyTotals,
  buildAllFarmerAnomalyRecords,
  computeDailyAnomalies,
  getAnomalyColor,
  getDaysInMonth,
} from "./analyticsHelpers";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const ANOMALY_LABELS = { 4: "Critical", 3: "High", 2: "Medium" };
const getAnomalyLabel = (absZ) => {
  if (absZ >= 4) return ANOMALY_LABELS[4];
  if (absZ >= 3) return ANOMALY_LABELS[3];
  return ANOMALY_LABELS[2];
};

const PAGE_SIZE_OPTIONS = [10, 20];

const AnomalyDot = ({ cx, cy, payload }) => {
  if (!payload?.isAnomaly) return null;
  const absZ = Math.abs(payload.zScore);
  const r = Math.min(8 + (absZ - 2) * 3, 20);
  return (
    <circle
      cx={cx}
      cy={cy}
      r={r}
      fill={getAnomalyColor(absZ)}
      opacity={0.8}
      stroke="#fff"
      strokeWidth={1.5}
    />
  );
};

const AnomalyTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow text-sm">
      <p className="font-medium text-gray-800">Day {d.day}</p>
      <p className="text-gray-600">{d.liters.toFixed(2)} L</p>
      <p className="text-gray-600">Farmer: {d.farmerDisplayName}</p>
      <p className="text-red-600 font-medium">
        Anomaly — z-score: {d.zScore.toFixed(2)} ({getAnomalyLabel(Math.abs(d.zScore))})
      </p>
    </div>
  );
};

const DailyTrendTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow text-sm">
      <p className="font-medium text-gray-800">Day {d.day}</p>
      <p className="text-cyan-700">{d.total.toFixed(2)} L collected</p>
      {d.isAnomaly && (
        <p className="text-red-600 font-medium">
          Anomaly — z-score: {d.zScore.toFixed(2)}
        </p>
      )}
    </div>
  );
};

const AnalyticsTab = ({ companyName }) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState("");
  const [error, setError] = useState("");
  const [analyzedRecords, setAnalyzedRecords] = useState([]);
  const [dailyCollectionData, setDailyCollectionData] = useState([]);
  const [anomalyPage, setAnomalyPage] = useState(1);
  const [recordPage, setRecordPage] = useState(1);
  const [anomalyPageSize, setAnomalyPageSize] = useState(10);
  const [recordPageSize, setRecordPageSize] = useState(10);

  const yearOptions = useMemo(
    () => Array.from({ length: 6 }, (_, i) => currentYear - i),
    [currentYear]
  );

  const anomalyRecords = useMemo(
    () => analyzedRecords.filter((record) => record.isAnomaly),
    [analyzedRecords]
  );

  const sortedRecords = useMemo(
    () =>
      [...analyzedRecords].sort((a, b) => {
        if (a.day !== b.day) return a.day - b.day;
        return a.farmerDisplayName.localeCompare(b.farmerDisplayName);
      }),
    [analyzedRecords]
  );

  const daysInMonthCount = useMemo(() => getDaysInMonth(year, month), [year, month]);

  const collectionSummary = useMemo(() => {
    const withData = dailyCollectionData.filter((d) => d.total > 0);
    const totalLitres = withData.reduce((sum, d) => sum + d.total, 0);
    const avgDaily = withData.length > 0 ? totalLitres / withData.length : 0;
    const peakDay =
      withData.length > 0
        ? withData.reduce((max, d) => (d.total > max.total ? d : max), withData[0])
        : null;
    const anomalyDayCount = dailyCollectionData.filter((d) => d.isAnomaly).length;
    return {
      totalLitres,
      avgDaily,
      peakDay,
      activeDays: withData.length,
      anomalyDayCount,
    };
  }, [dailyCollectionData]);

  const anomalyTotalPages = Math.max(
    1,
    Math.ceil(anomalyRecords.length / anomalyPageSize)
  );
  const recordsTotalPages = Math.max(
    1,
    Math.ceil(sortedRecords.length / recordPageSize)
  );
  const paginatedAnomalies = anomalyRecords.slice(
    (anomalyPage - 1) * anomalyPageSize,
    anomalyPage * anomalyPageSize
  );
  const paginatedRecords = sortedRecords.slice(
    (recordPage - 1) * recordPageSize,
    recordPage * recordPageSize
  );

  const analyze = useCallback(async () => {
    if (!companyName || !db) return;
    setLoading(true);
    setLoadingProgress("");
    setError("");
    setAnalyzedRecords([]);
    setDailyCollectionData([]);
    setAnomalyPage(1);
    setRecordPage(1);
    try {
      const farmersRef = ref(db, `Companies/${companyName}/ValidatedFarmers`);
      const milkCollectedRef = ref(
        db,
        `Companies/${companyName}/MilkCollected/${year}/${month}`
      );

      const [farmersSnapshot, milkCollectedSnapshot] = await Promise.all([
        get(farmersRef),
        get(milkCollectedRef),
      ]);

      const farmersMap = farmersSnapshot.exists() ? farmersSnapshot.val() : {};
      const farmerKeys = Object.keys(farmersMap);

      setLoadingProgress(`Analyzing ${farmerKeys.length} farmer${farmerKeys.length !== 1 ? "s" : ""}…`);

      const farmerRecordsMap = {};
      await Promise.all(
        farmerKeys.map(async (otherName) => {
          const recordsRef = ref(
            db,
            `Companies/${companyName}/FarmerRecords/${otherName}/${year}/${month}`
          );
          const recordsSnapshot = await get(recordsRef);
          if (recordsSnapshot.exists()) {
            farmerRecordsMap[otherName] = recordsSnapshot.val();
          }
        })
      );

      const records = buildAllFarmerAnomalyRecords(farmersMap, farmerRecordsMap, year, month);
      setAnalyzedRecords(records);

      const dayTotals = aggregateDailyTotals(milkCollectedSnapshot.val());
      const daysInMonth = getDaysInMonth(year, month);
      const dailyAnomalies = computeDailyAnomalies(dayTotals, daysInMonth);
      setDailyCollectionData(dailyAnomalies);
    } catch (err) {
      console.error("Failed to load milk collection data", err);
      setError("Unable to load data. Please try again.");
    } finally {
      setLoading(false);
      setLoadingProgress("");
    }
  }, [companyName, year, month]);

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-cyan-700 mb-2">
          📊 Reports & Insights
        </h2>
        <p className="text-gray-600">
          Select a month and year to analyze farmer milk records and detect anomalous entries.
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm text-gray-700">
          <span className="block mb-1 font-medium">Month</span>
          <select
            className="border border-gray-300 rounded-lg px-3 py-2 bg-white"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          >
            {MONTH_NAMES.map((name, idx) => (
              <option key={idx + 1} value={idx + 1}>
                {name}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm text-gray-700">
          <span className="block mb-1 font-medium">Year</span>
          <select
            className="border border-gray-300 rounded-lg px-3 py-2 bg-white"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {yearOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          onClick={analyze}
          disabled={!companyName || loading}
          className="bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-400 text-white rounded-lg px-4 py-2"
        >
          {loading ? "Analyzing…" : "Analyze"}
        </button>
      </div>

      {/* Loading progress */}
      {loading && loadingProgress ? (
        <div className="text-sm text-cyan-700 bg-cyan-50 border border-cyan-100 rounded-lg px-4 py-2">
          {loadingProgress}
        </div>
      ) : null}

      {/* Error banner */}
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
          {error}
        </div>
      ) : null}

      {/* Chart */}
      {analyzedRecords.length > 0 && (
        <>
          <div className="bg-white border border-gray-100 rounded-lg p-4 shadow-sm">
            <h3 className="text-sm font-medium text-gray-700 mb-1">
              Farmer Anomalies — {MONTH_NAMES[month - 1]} {year}
            </h3>
            <p className="text-xs text-gray-400 mb-3">
              Anomaly dots show the suspicious farmer record by day (x-axis) and litres (y-axis).
            </p>

            {/* Legend */}
            <div className="flex flex-wrap gap-3 mb-3 text-xs text-gray-600">
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded-full bg-[#f97316]" />
                Medium (z ≥ 2)
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded-full bg-[#ea580c]" />
                High (z ≥ 3)
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded-full bg-[#dc2626]" />
                Critical (z ≥ 4)
              </span>
            </div>

            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart
                  margin={{ top: 10, right: 20, bottom: 20, left: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="day"
                    type="number"
                    domain={[1, 31]}
                    allowDecimals={false}
                    label={{ value: "Day of Month", position: "insideBottom", offset: -10 }}
                  />
                  <YAxis
                    dataKey="liters"
                    type="number"
                    label={{ value: "Litres", angle: -90, position: "insideLeft", offset: 10 }}
                  />
                  <Tooltip content={<AnomalyTooltip />} />
                  <Scatter
                    data={anomalyRecords}
                    fill="#ea580c"
                    dot={<AnomalyDot />}
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Anomaly records list */}
          <div className="bg-white border border-gray-100 rounded-lg p-4 shadow-sm">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Anomaly Records
              <span className="ml-2 font-normal text-gray-500">
                ({anomalyRecords.length} detected)
              </span>
            </h3>

            {anomalyRecords.length === 0 ? (
              <p className="text-sm text-gray-500">
                No anomalies detected for this period.
              </p>
            ) : (
              <>
                <div className="flex justify-end mb-2">
                  <label className="text-xs text-gray-600">
                    Rows:
                    <select
                      className="ml-2 border border-gray-300 rounded px-2 py-1"
                      value={anomalyPageSize}
                      onChange={(e) => {
                        setAnomalyPageSize(Number(e.target.value));
                        setAnomalyPage(1);
                      }}
                    >
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead>
                      <tr className="border-b border-gray-100 text-gray-500">
                        <th className="py-2 pr-4 font-medium">Day</th>
                        <th className="py-2 pr-4 font-medium">Farmer</th>
                        <th className="py-2 pr-4 font-medium">Litres (L)</th>
                        <th className="py-2 pr-4 font-medium">Z-Score</th>
                        <th className="py-2 font-medium">Severity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedAnomalies.map((record) => {
                        const absZ = Math.abs(record.zScore);
                        const color = getAnomalyColor(absZ);
                        return (
                          <tr
                            key={`${record.farmerKey}-${record.day}`}
                            className="border-b border-gray-50 hover:bg-gray-50"
                          >
                            <td className="py-2 pr-4 font-medium text-gray-800">
                              {record.day}
                            </td>
                            <td className="py-2 pr-4 text-gray-700">
                              {record.farmerDisplayName}
                            </td>
                            <td className="py-2 pr-4 text-gray-700">
                              {record.liters.toFixed(2)}
                            </td>
                            <td className="py-2 pr-4 text-gray-700">
                              {record.zScore.toFixed(2)}
                            </td>
                            <td className="py-2">
                              <span
                                className="px-2 py-0.5 rounded-full text-white text-xs font-medium"
                                style={{ backgroundColor: color }}
                              >
                                {getAnomalyLabel(absZ)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {anomalyTotalPages > 1 && (
                  <div className="flex items-center justify-center gap-3 mt-4">
                    <button
                      type="button"
                      onClick={() => setAnomalyPage((p) => Math.max(1, p - 1))}
                      disabled={anomalyPage === 1}
                      className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
                    >
                      ← Prev
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {anomalyPage} of {anomalyTotalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setAnomalyPage((p) => Math.min(anomalyTotalPages, p + 1))
                      }
                      disabled={anomalyPage === anomalyTotalPages}
                      className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Daily Milk Collection Trend */}
          {dailyCollectionData.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-lg p-4 shadow-sm">
              <h3 className="text-sm font-medium text-gray-700 mb-1">
                Daily Milk Collection Trend — {MONTH_NAMES[month - 1]} {year}
              </h3>
              <p className="text-xs text-gray-400 mb-3">
                Total litres collected across all farmers per day. Red dots mark statistically anomalous collection days.
              </p>

              {/* Summary stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="bg-cyan-50 rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-500">Total Collected</div>
                  <div className="text-lg font-semibold text-cyan-700">
                    {collectionSummary.totalLitres.toFixed(1)} L
                  </div>
                </div>
                <div className="bg-cyan-50 rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-500">Avg Daily</div>
                  <div className="text-lg font-semibold text-cyan-700">
                    {collectionSummary.avgDaily.toFixed(1)} L
                  </div>
                </div>
                <div className="bg-cyan-50 rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-500">Active Days</div>
                  <div className="text-lg font-semibold text-cyan-700">
                    {collectionSummary.activeDays}
                  </div>
                </div>
                <div className="bg-cyan-50 rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-500">Peak Day</div>
                  <div className="text-lg font-semibold text-cyan-700">
                    {collectionSummary.peakDay
                      ? `Day ${collectionSummary.peakDay.day} (${collectionSummary.peakDay.total.toFixed(1)} L)`
                      : "—"}
                  </div>
                </div>
              </div>

              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={dailyCollectionData}
                    margin={{ top: 10, right: 20, bottom: 20, left: 10 }}
                  >
                    <defs>
                      <linearGradient id="colorTotalLitres" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0891b2" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#0891b2" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" />
                    <XAxis
                      dataKey="day"
                      type="number"
                      domain={[1, daysInMonthCount]}
                      allowDecimals={false}
                      tickCount={daysInMonthCount}
                      label={{ value: "Day of Month", position: "insideBottom", offset: -10 }}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis
                      label={{ value: "Litres", angle: -90, position: "insideLeft", offset: 10 }}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip content={<DailyTrendTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="total"
                      stroke="#0891b2"
                      strokeWidth={2}
                      fill="url(#colorTotalLitres)"
                      dot={(props) => {
                        const { cx, cy, payload } = props;
                        if (payload.isAnomaly) {
                          return (
                            <circle
                              key={`dot-${payload.day}`}
                              cx={cx}
                              cy={cy}
                              r={5}
                              fill="#dc2626"
                              stroke="#fff"
                              strokeWidth={1.5}
                            />
                          );
                        }
                        if (payload.total > 0) {
                          return (
                            <circle
                              key={`dot-${payload.day}`}
                              cx={cx}
                              cy={cy}
                              r={3}
                              fill="#0891b2"
                              stroke="#fff"
                              strokeWidth={1}
                            />
                          );
                        }
                        return null;
                      }}
                      activeDot={{ r: 6 }}
                      name="Litres Collected"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {collectionSummary.anomalyDayCount > 0 && (
                <p className="text-xs text-red-600 mt-2">
                  ⚠ {collectionSummary.anomalyDayCount} anomalous day{collectionSummary.anomalyDayCount !== 1 ? "s" : ""} detected (red dots).
                </p>
              )}
            </div>
          )}

          <div className="bg-white border border-gray-100 rounded-lg p-4 shadow-sm">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Analyzed Farmer Records
              <span className="ml-2 font-normal text-gray-500">
                ({sortedRecords.length} records)
              </span>
            </h3>
            <div className="flex justify-end mb-2">
              <label className="text-xs text-gray-600">
                Rows:
                <select
                  className="ml-2 border border-gray-300 rounded px-2 py-1"
                  value={recordPageSize}
                  onChange={(e) => {
                    setRecordPageSize(Number(e.target.value));
                    setRecordPage(1);
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="border-b border-gray-100 text-gray-500">
                    <th className="py-2 pr-4 font-medium">Day</th>
                    <th className="py-2 pr-4 font-medium">Farmer</th>
                    <th className="py-2 pr-4 font-medium">Litres (L)</th>
                    <th className="py-2 pr-4 font-medium">Z-Score</th>
                    <th className="py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRecords.map((record) => (
                    <tr
                      key={`${record.farmerKey}-${record.day}`}
                      className="border-b border-gray-50 hover:bg-gray-50"
                    >
                      <td className="py-2 pr-4 font-medium text-gray-800">{record.day}</td>
                      <td className="py-2 pr-4 text-gray-700">{record.farmerDisplayName}</td>
                      <td className="py-2 pr-4 text-gray-700">{record.liters.toFixed(2)}</td>
                      <td className="py-2 pr-4 text-gray-700">{record.zScore.toFixed(2)}</td>
                      <td className="py-2">
                        {record.isAnomaly ? (
                          <span className="px-2 py-0.5 rounded-full text-white text-xs font-medium bg-red-600">
                            Anomaly
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            Normal
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {recordsTotalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => setRecordPage((p) => Math.max(1, p - 1))}
                  disabled={recordPage === 1}
                  className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
                >
                  ← Prev
                </button>
                <span className="text-sm text-gray-600">
                  Page {recordPage} of {recordsTotalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setRecordPage((p) => Math.min(recordsTotalPages, p + 1))}
                  disabled={recordPage === recordsTotalPages}
                  className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Empty state */}
      {!analyzedRecords.length && !loading && !error && (
        <div className="text-sm text-gray-500 bg-white border border-gray-100 rounded-lg p-4">
          Select a month and year, then click{" "}
          <span className="font-medium">Analyze</span> to view daily milk
          collection anomalies.
        </div>
      )}
    </div>
  );
};

export default AnalyticsTab;
